import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraDashboardPage, SpecraEmptyState, SpecraInlineBanner, SpecraPageHeader, SpecraSectionHeader, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useOpenCorrectiveActions } from '../../../components/delivery/widgets/useOpenCorrectiveActions';
import { PageGrid } from '../../../components/layout';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { RollupCard } from './cards/RollupCard';
import { FilterBar } from './FilterBar';
import { fmtCount, fmtUsd } from './format';
import { CURRENT_PERIOD_COUNTS, sumCounts, useDeliverableCounts, useDeliveryRollups, usePortfolioDashboard } from './hooks';
import { ProgramHealthTable } from './ProgramHealthTable';
/**
 * The deliverable counts of one roll-up scope: Σ over the grid's programs in
 * it. A program belongs to a scope by the scope's id ("" = the unassigned
 * bucket), never by name: two scopes can share a display name (D114 #5).
 */ function scopeDeliverables(rollup, programs, scopeOf, fold) {
    const ids = programs.filter((p)=>scopeOf(p) === rollup.scopeId).map((p)=>p.programId);
    const readable = ids.filter((id)=>!fold.failedIds.has(id));
    if (ids.length > 0 && readable.length === 0) return {
        isError: true
    };
    if (readable.some((id)=>!fold.byProgram.has(id))) return {};
    return {
        counts: sumCounts(readable.flatMap((id)=>fold.byProgram.get(id) ?? []))
    };
}
const EMPTY_SCOPE = {
    sectorId: '',
    businessUnitId: '',
    portfolioId: '',
    contractState: ''
};
export function CommandCenterPage() {
    const companyId = useTenantId();
    const [scope, setScope] = useState(EMPTY_SCOPE);
    const filter = {
        companyId,
        ...scope
    };
    const rollups = useDeliveryRollups(filter);
    const dashboard = usePortfolioDashboard(filter);
    // ONE tenant-wide open-Corrections rollup for the whole page, grouped by
    // program here — same pattern as the on-time report above, and the SAME RPC
    // Home's DeliverySummaryCard already reads (GetOpenCorrectiveActionCounts).
    // No second count is computed here.
    const openCorrectiveActions = useOpenCorrectiveActions({
        companyId
    });
    const openCorrectiveActionCountsByProgram = useMemo(()=>{
        const byProgram = new Map();
        for (const c of openCorrectiveActions.data?.counts ?? []){
            byProgram.set(c.programId, Number(c.openCount));
        }
        return byProgram;
    }, [
        openCorrectiveActions.data
    ]);
    const portfolioRollups = rollups.data?.portfolioRollups ?? [];
    const sectorRollups = rollups.data?.sectorRollups ?? [];
    const programs = dashboard.data?.programs ?? [];
    const hiddenCount = rollups.data?.hiddenCount ?? 0;
    const programIds = useMemo(()=>programs.map((p)=>p.programId), [
        programs
    ]);
    const deliverables = useDeliverableCounts(programIds, CURRENT_PERIOD_COUNTS);
    // The scope folds need the program list: until it settles a card has
    // nothing to sum, which must not read as "No approvals yet".
    const scopeFold = (scopeOf)=>(r)=>dashboard.isError ? {
                isError: true
            } : dashboard.isLoading ? {} : scopeDeliverables(r, programs, scopeOf, deliverables);
    // A failed rollup/dashboard read must read as "broken," not "empty" — an
    // empty org and a transport/auth/projection failure look identical
    // otherwise. Retry refetches both queries.
    const hasError = rollups.isError || dashboard.isError;
    const isSettled = !rollups.isLoading && !dashboard.isLoading;
    const retry = ()=>{
        if (rollups.isError) rollups.refetch();
        if (dashboard.isError) dashboard.refetch();
    };
    // EntityTable emits page-state-content when programs.length > 0. The sentinel
    // covers the two cases EntityTable cannot: still-loading (page-state-loading so
    // the UX gate knows not to race yet) and settled-but-empty (page-state-content
    // so the gate resolves instead of spinning). When programs are present EntityTable
    // owns page-state-content and the sentinel is hidden to avoid duplicate testids.
    const sentinelTestid = !isSettled ? Testids.pageState.loading : programs.length === 0 ? Testids.pageState.content : undefined;
    const d = dashboard.data;
    const summaryMetrics = [
        {
            id: 'programs',
            label: 'Programs',
            value: fmtCount(d?.totalPrograms)
        },
        {
            id: 'tcv',
            label: 'Total TCV',
            value: fmtUsd(d?.totalTcv?.value)
        },
        {
            id: 'funded',
            label: 'Funded',
            value: fmtUsd(d?.totalFunded?.value)
        },
        {
            id: 'ftes',
            label: 'FTEs',
            value: fmtCount(d?.totalFtes?.value)
        },
        {
            id: 'kpi-compliant',
            label: 'KPI compliant (1/2/3/4)',
            value: `${fmtCount(d?.kpi1CompliantCount)} / ${fmtCount(d?.kpi2CompliantCount)} / ${fmtCount(d?.kpi3CompliantCount)} / ${fmtCount(d?.kpi4CompliantCount)}`
        }
    ];
    return /*#__PURE__*/ _jsxs(SpecraDashboardPage, {
        "data-testid": Testids.commandCenter.page,
        header: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(SpecraPageHeader, {
                    description: "Cross-project delivery intelligence across all authorized portfolios and sectors.",
                    title: "Command Center"
                }),
                /*#__PURE__*/ _jsx(FilterBar, {
                    companyId: companyId,
                    value: scope,
                    onChange: setScope
                }),
                hasError && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        iconLeft: /*#__PURE__*/ _jsx(RefreshCw, {}),
                        onClick: retry,
                        size: "sm",
                        variant: "secondary",
                        children: "Retry"
                    }),
                    "data-testid": Testids.commandCenter.errorBanner,
                    tone: "danger",
                    variant: "framed",
                    children: "Couldn’t load delivery rollups. This is a load error, not an empty portfolio — totals and cards below may be incomplete."
                })
            ]
        }),
        metrics: {
            'data-testid': Testids.commandCenter.summary,
            density: 'compact',
            metrics: summaryMetrics
        },
        children: [
            sentinelTestid && /*#__PURE__*/ _jsx("span", {
                className: "hidden",
                "data-testid": sentinelTestid,
                "aria-hidden": "true"
            }),
            hiddenCount > 0 && /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                "data-testid": Testids.commandCenter.hiddenCount,
                size: "caption",
                tone: "muted",
                children: [
                    hiddenCount,
                    " ",
                    hiddenCount === 1 ? 'program is' : 'programs are',
                    " hidden from these totals because you don’t have access to ",
                    hiddenCount === 1 ? 'it' : 'them',
                    "."
                ]
            }),
            deliverables.failed > 0 && /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                "data-testid": Testids.cmPortfolio.partialNote,
                size: "caption",
                tone: "danger",
                children: [
                    "Deliverable figures could not be read for ",
                    deliverables.failed,
                    ' ',
                    deliverables.failed === 1 ? 'program' : 'programs',
                    "; the on-time and late totals leave",
                    ' ',
                    deliverables.failed === 1 ? 'it' : 'them',
                    " out."
                ]
            }),
            /*#__PURE__*/ _jsx(RollupSection, {
                title: "Portfolios",
                testid: Testids.commandCenter.portfolioRollups,
                rollups: portfolioRollups,
                deliverablesOf: scopeFold((p)=>p.portfolioId),
                isLoading: rollups.isLoading,
                isError: rollups.isError
            }),
            /*#__PURE__*/ _jsx(RollupSection, {
                title: "Sectors",
                testid: Testids.commandCenter.sectorRollups,
                rollups: sectorRollups,
                deliverablesOf: scopeFold((p)=>p.sectorId),
                isLoading: rollups.isLoading,
                isError: rollups.isError
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        headingAs: "h2",
                        title: "Program health",
                        variant: "group"
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "caption",
                        tone: "muted",
                        children: "On-time and late count each program's deliverables in its current contract period."
                    }),
                    /*#__PURE__*/ _jsx(ProgramHealthTable, {
                        rows: programs,
                        isLoading: dashboard.isLoading,
                        isError: dashboard.isError,
                        onRetry: dashboard.isError ? dashboard.refetch : undefined,
                        deliverables: deliverables,
                        openCorrectionCountsByProgram: openCorrectiveActionCountsByProgram,
                        openCorrectionsLoading: openCorrectiveActions.isLoading,
                        openCorrectionsError: openCorrectiveActions.isError
                    })
                ]
            })
        ]
    });
}
function RollupSection({ title, testid, rollups, deliverablesOf, isLoading, isError }) {
    // Backend order is by scopeId (a UUID) — meaningless for display, and
    // zero-fill means empty scopes now interleave randomly among ones that
    // carry real work. Sort for render only: populated scopes first, then
    // alphabetically by name (the "Unassigned" bucket included, by its own
    // rendered name like anything else).
    const sortedRollups = [
        ...rollups
    ].sort((a, b)=>{
        if (a.programCount === 0 !== (b.programCount === 0)) {
            return a.programCount === 0 ? 1 : -1;
        }
        return (a.scopeName || 'Unassigned').localeCompare(b.scopeName || 'Unassigned', undefined, {
            sensitivity: 'base'
        });
    });
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2",
        "data-testid": testid,
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                headingAs: "h2",
                title: title,
                variant: "group"
            }),
            isLoading ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                tone: "muted",
                children: "Loading…"
            }) : isError ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                tone: "muted",
                children: "—"
            }) : rollups.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                framed: true,
                size: "compact",
                title: `No ${title.toLowerCase()} in scope.`
            }) : /*#__PURE__*/ _jsx(PageGrid, {
                children: sortedRollups.map((r)=>/*#__PURE__*/ _jsx(RollupCard, {
                        rollup: r,
                        deliverables: deliverablesOf(r)
                    }, r.scopeId || 'unassigned'))
            })
        ]
    });
}
